import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ROOM_NOTES_EVERY_ROUNDS, normalizeRoomNotes, notesLastLine, roomNotesFileName, roomNotesToMarkdown,
} from '../../utils/roomNotes';

// 📝 Notes that write themselves (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.9;
// mockup room-toolkit.html §4, option A). A Productivity or Research room's
// chapter pass fills three lists every 5 rounds: Decisions · To-dos · Open
// questions. Read only, v1: "Summarize now" needs a rate-limited backend
// (next), and ticking a to-do needs a write (portedTasks' pattern, next).
//
// Props contract (the room frame mounts it; this file never fetches):
//   notes        { decisions: string[], todos: [{ text, owner? }], questions: string[] } | null
//                (utils/roomNotes.pickLatestRoomNotes(actions).notes; null = the empty state)
//   updatedAt    ISO string | null   the chapter row's created_at
//   roomName     string              the room's name (the export's title + file name)
//   throughRound number | null       optional: "last at round 15"
//   isMobile     bool                false: an inline block for the desktop rail
//                                    true: a bottom sheet, drawn only while `open`
//   open, onClose                    the phone sheet's state (scrim, ✕ and Esc call onClose)
//   tone         'dark' | 'light'    the frame's theme (default dark)
//   accent       css color | null    the room's one accent (dots, ?)
//   now          Date | null         tests only: freezes "4 min ago"
//
// Out of the theme's reach: inside rooms the generated customCSS styles every
// button and any class containing card/panel/chip/message/btn/badge/tag/…, so
// every class here is `rnt-*`, the buttons carry the theme shield
// (data-prop-ui) plus doubled-class !important armor, and every padded box
// sets its own box-sizing (the app has no global border-box).
const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const BTN = `all: unset; box-sizing: border-box !important; display: inline-flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
  margin: 0 !important; min-width: 0 !important; min-height: 0 !important; max-width: none !important; max-height: none !important; text-transform: none !important; letter-spacing: 0 !important;
  box-shadow: none !important; text-shadow: none !important; transform: none !important; opacity: 1 !important;`;
export const NOTES_PANEL_CSS = `
.rnt, .rnt *, .rnt-layer, .rnt-layer * { box-sizing: border-box; }
.rnt { --rnt-ink: #ECEEF2; --rnt-muted: #8B92A0; --rnt-line: #262B34; --rnt-line2: #363C48; --rnt-bg: #12151A; --rnt-row: #1A1E25; --rnt-sheetbg: #151920; --rnt-accent: #6EA8FE;
  color: var(--rnt-ink); font: 400 13.5px/1.45 ${SANS}; text-align: left; overflow-wrap: anywhere; }
.rnt[data-tone="light"] { --rnt-ink: #111317; --rnt-muted: #5E6573; --rnt-line: #E3E6EB; --rnt-line2: #D0D5DD; --rnt-bg: #FFFFFF; --rnt-row: #F2F4F7; --rnt-sheetbg: #FFFFFF; --rnt-accent: #2F6FD6; }
.rnt-inline { display: flex; flex-direction: column; min-height: 0; width: 100%; padding: 14px 16px; border-radius: 12px; background: var(--rnt-bg); box-shadow: inset 0 0 0 1px var(--rnt-line); }
.rnt-top { display: flex; align-items: center; gap: 8px; min-height: 32px; }
.rnt-tic { display: inline-flex; flex: none; color: var(--rnt-accent); }
.rnt .rnt-title.rnt-title { margin: 0 !important; padding: 0 !important; font: 600 15px/1.2 ${SANS} !important; color: var(--rnt-ink) !important; letter-spacing: 0 !important; text-transform: none !important; text-shadow: none !important; border: 0 !important; background: none !important; }
.rnt-sheet .rnt-title.rnt-title { font-size: 19px !important; }
.rnt-sp { flex: 1; min-width: 0; }
.rnt .rnt-exp.rnt-exp { ${BTN} width: auto !important; height: 32px !important; padding: 0 10px !important; border-radius: 8px !important; border: 1px solid var(--rnt-line2) !important; background: transparent !important; color: var(--rnt-ink) !important; font: 500 12.5px/1 ${SANS} !important; white-space: nowrap; }
.rnt .rnt-exp.rnt-exp:hover { background: var(--rnt-row) !important; }
.rnt .rnt-x.rnt-x { ${BTN} width: 40px !important; height: 40px !important; flex: none; padding: 0 !important; border-radius: 10px !important; border: 0 !important; background: transparent !important; color: var(--rnt-muted) !important; }
.rnt .rnt-x.rnt-x:hover { background: var(--rnt-row) !important; color: var(--rnt-ink) !important; }
.rnt .rnt-exp.rnt-exp:focus-visible, .rnt .rnt-x.rnt-x:focus-visible { outline: 2px solid var(--rnt-accent) !important; outline-offset: 2px; }
.rnt-upd { display: flex; align-items: center; gap: 6px; margin-top: 8px; font: 500 11px/1.35 ${MONO}; color: var(--rnt-muted); }
.rnt-upd svg { flex: none; }
.rnt-sec { margin-top: 14px; }
.rnt .rnt-h.rnt-h { display: flex; align-items: center; gap: 8px; margin: 0 0 4px !important; padding: 0 !important; font: 500 10.5px/1.2 ${MONO} !important; letter-spacing: .09em !important; text-transform: uppercase !important; color: var(--rnt-muted) !important; border: 0 !important; background: none !important; text-shadow: none !important; }
.rnt-hic { display: grid; place-items: center; width: 18px; height: 18px; border-radius: 5px; background: var(--rnt-row); color: var(--rnt-ink); flex: none; }
.rnt .rnt-list.rnt-list { list-style: none !important; margin: 0 !important; padding: 0 !important; }
.rnt .rnt-i.rnt-i { display: flex !important; gap: 10px; align-items: flex-start; list-style: none !important; margin: 0 !important; padding: 7px 0 !important; font: 400 13.5px/1.4 ${SANS}; color: var(--rnt-ink); background: none !important; border: 0 !important; }
.rnt .rnt-i.rnt-i::before { content: none !important; }
.rnt-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rnt-accent); margin-top: 7px; flex: none; }
.rnt-ring { width: 8px; height: 8px; border-radius: 50%; box-shadow: inset 0 0 0 1.5px var(--rnt-accent); margin-top: 6px; flex: none; }
.rnt-qm { width: 10px; flex: none; color: var(--rnt-accent); font-weight: 700; }
.rnt-txt { min-width: 0; }
.rnt-own { display: block; margin-top: 2px; font: 500 11px/1.3 ${MONO}; color: var(--rnt-muted); }
.rnt-none { padding: 4px 0 2px; font-size: 12.5px; color: var(--rnt-muted); }
.rnt-empty { margin-top: 14px; padding: 16px 12px; border-radius: 10px; background: var(--rnt-row); color: var(--rnt-muted); font-size: 13.5px; text-align: center; }
.rnt-foot { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--rnt-line); font-size: 11.5px; line-height: 1.4; color: var(--rnt-muted); }
.rnt-layer { position: fixed; inset: 0; z-index: 150020; display: flex; flex-direction: column; justify-content: flex-end; }
.rnt-scrim { position: absolute; inset: 0; background: rgba(5,6,9,.62); }
.rnt-sheet { position: relative; display: flex; flex-direction: column; width: 100%; max-width: 100%; max-height: 82vh; max-height: 82dvh; margin: 0 auto;
  padding: 6px 16px calc(16px + env(safe-area-inset-bottom, 0px)); border-radius: 18px 18px 0 0; background: var(--rnt-sheetbg); box-shadow: 0 -12px 40px rgba(0,0,0,.45); animation: rntUp .2s ease-out; outline: none; }
.rnt-grab { display: block; width: 40px; height: 5px; border-radius: 3px; background: var(--rnt-line2); margin: 4px auto 6px; flex: none; }
.rnt-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
@media (min-width: 700px) { .rnt-sheet { max-width: 560px; } }
@keyframes rntUp { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .rnt-sheet { animation: none; } }
`;

const Icon = ({ d, size = 14, stroke = 2, circle = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {circle ? <circle cx="12" cy="12" r="8.5" /> : null}
    <path d={d} />
  </svg>
);
const I = {
  notes: 'M6 3h11.5a1.5 1.5 0 0 1 1.5 1.5v15a1.5 1.5 0 0 1-1.5 1.5H6zM9.5 3v18M12.5 8h3.5M12.5 12h3.5',
  download: 'M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14',
  clock: 'M12 7.5V12l3 2',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  list: 'M9 7h10M9 12h10M9 17h10M5 7h.01M5 12h.01M5 17h.01',
  help: 'M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.3-2.4 3.6M12 17h.01',
  x: 'M6 6l12 12M18 6 6 18',
};

function downloadNotes({ notes, roomName, updatedAt, throughRound }) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const at = new Date();
  const blob = new Blob([roomNotesToMarkdown(notes, { roomName, updatedAt, throughRound, now: at })], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = roomNotesFileName(roomName, at);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* gone already */ } }, 1000);
}

function Section({ icon, title, items, render }) {
  return (
    <div className="rnt-sec">
      <h4 className="rnt-h"><i className="rnt-hic"><Icon d={icon} size={11} stroke={2.6} /></i>{title} · {items.length}</h4>
      {items.length ? <ul className="rnt-list">{items.map(render)}</ul> : <div className="rnt-none">Nothing yet.</div>}
    </div>
  );
}

function NotesBody({ notes, updatedAt, throughRound, now }) {
  const last = notes ? notesLastLine(updatedAt, throughRound, now) : '';
  return (
    <>
      <div className="rnt-upd"><Icon d={I.clock} size={12} stroke={2.2} circle />Updated after every {ROOM_NOTES_EVERY_ROUNDS} rounds{last ? ` · ${last}` : ''}</div>
      {!notes ? (
        <div className="rnt-empty">Notes show up after a few rounds of talk.</div>
      ) : (
        <>
          <Section icon={I.check} title="Decisions" items={notes.decisions} render={(t, i) => (
            <li key={`d${i}`} className="rnt-i"><span className="rnt-dot" /><span className="rnt-txt">{t}</span></li>
          )} />
          <Section icon={I.list} title="To-dos" items={notes.todos} render={(t, i) => (
            <li key={`t${i}`} className="rnt-i"><span className="rnt-ring" /><span className="rnt-txt">{t.text}{t.owner ? <small className="rnt-own">{t.owner}</small> : null}</span></li>
          )} />
          <Section icon={I.help} title="Open questions" items={notes.questions} render={(t, i) => (
            <li key={`q${i}`} className="rnt-i"><span className="rnt-qm" aria-hidden="true">?</span><span className="rnt-txt">{t}</span></li>
          )} />
        </>
      )}
      <div className="rnt-foot">Written by a small AI note-taker from this chat. It can be wrong.</div>
    </>
  );
}

export default function NotesPanel({
  notes: rawNotes = null,
  updatedAt = null,
  roomName = '',
  throughRound = null,
  isMobile = false,
  open = false,
  onClose = null,
  tone = 'dark',
  accent = null,
  now: nowProp = null,
}) {
  const notes = normalizeRoomNotes(rawNotes);
  const titleId = `rnt-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // "4 min ago" stays true while the panel sits open
  const [, setTick] = useState(0);
  const hasNotes = !!notes;
  useEffect(() => {
    if (nowProp || !hasNotes || !updatedAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [nowProp, hasNotes, updatedAt]);
  const sheetOpen = !!(isMobile && open);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    // focus goes back where it came from when the sheet closes (FilesFolder's idiom)
    const back = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape' && onCloseRef.current) onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      if (back && back !== document.body && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (e) { /* gone */ }
      }
    };
  }, [sheetOpen]);

  const now = nowProp || new Date();
  const toneAttr = tone === 'light' ? 'light' : 'dark';
  const style = accent ? { '--rnt-accent': accent } : undefined;
  const exportBtn = notes ? (
    <button
      type="button"
      data-prop-ui=""
      className="rnt-exp"
      style={{ '--prop-bg': 'transparent' }}
      onClick={() => downloadNotes({ notes, roomName, updatedAt, throughRound })}
    >
      <Icon d={I.download} size={14} stroke={2.2} />Export notes
    </button>
  ) : null;
  const top = (
    <div className="rnt-top">
      <span className="rnt-tic"><Icon d={I.notes} size={isMobile ? 17 : 14} /></span>
      <h3 className="rnt-title" id={titleId}>Notes</h3>
      <span className="rnt-sp" />
      {exportBtn}
      {isMobile ? (
        <button ref={closeRef} type="button" data-prop-ui="" className="rnt-x" style={{ '--prop-bg': 'transparent' }} aria-label="Close notes" onClick={() => onClose && onClose()}>
          <Icon d={I.x} size={18} />
        </button>
      ) : null}
    </div>
  );
  const body = <NotesBody notes={notes} updatedAt={updatedAt} throughRound={throughRound} now={now} />;

  if (!isMobile) {
    return (
      <section className="rnt rnt-inline" data-tone={toneAttr} data-room-notes="" aria-labelledby={titleId} style={style}>
        <style>{NOTES_PANEL_CSS}</style>
        {top}
        {body}
      </section>
    );
  }
  if (!sheetOpen || typeof document === 'undefined') return null;
  return createPortal(
    <div className="rnt-layer" data-room-notes="">
      <style>{NOTES_PANEL_CSS}</style>
      <div className="rnt-scrim" aria-hidden="true" onClick={() => onClose && onClose()} />
      <div className="rnt rnt-sheet" data-tone={toneAttr} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={style}>
        <i className="rnt-grab" aria-hidden="true" />
        {top}
        <div className="rnt-body">{body}</div>
      </div>
    </div>,
    document.body,
  );
}
